import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  /** 为 true 时不弹出全局 Message.error */
  skipGlobalErrorMessage?: boolean;
}
import { Message, Modal } from '@arco-design/web-vue';
import { useUserStore } from '@/store';
import { getToken } from '@/utils/auth';
import { getApiBaseUrl, getApiBaseUrlA } from '@/utils/env';

export interface HttpResponse<T = unknown> {
  status?: number;
  msg?: string;
  message?: string;
  code: number;
  data: T;
}

const apiBaseUrl = getApiBaseUrl();
if (apiBaseUrl) {
  axios.defaults.baseURL = apiBaseUrl;
}

const apiBaseUrlA = getApiBaseUrlA();
export const axiosA = axios.create({
  baseURL: apiBaseUrlA || undefined,
});

function attachInterceptors(client: AxiosInstance) {
  client.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      const token = getToken();
      if (token) {
        if (!config.headers) {
          config.headers = {};
        }
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response: AxiosResponse<HttpResponse>) => {
      const res = response.data;
      // Success codes: 200 (standard HTTP success) or 20000 (legacy custom success)
      const isSuccess = res.code === 200 || res.code === 20000;
      if (!isSuccess) {
        const errorMsg = res.message || res.msg || 'Error';
        const skipGlobalErrorMessage = (
          response.config as CustomAxiosRequestConfig
        ).skipGlobalErrorMessage;

        if (errorMsg.includes('登录无效')) {
          const userStore = useUserStore();
          userStore.logout();
          window.location.href = '/login';
          return Promise.reject(new Error(errorMsg));
        }

        if (!skipGlobalErrorMessage) {
          Message.error({
            content: errorMsg,
            duration: 5 * 1000,
          });
        }

        if (
          [50008, 50012, 50014].includes(res.code) &&
          response.config.url !== '/api/account/profile'
        ) {
          Modal.error({
            title: 'Confirm logout',
            content:
              'You have been logged out, you can cancel to stay on this page, or log in again',
            okText: 'Re-Login',
            maskClosable: false,
            closable: true,
            simple: false,
            async onOk() {
              const userStore = useUserStore();

              await userStore.logout();
              window.location.reload();
            },
          });
        }
        return Promise.reject(new Error(errorMsg));
      }
      return res;
    },
    (error) => {
      const errorMessage =
        error.response?.data?.message || error.message || error.msg || 'Request Error';
      const skipGlobalErrorMessage = (
        error.config as CustomAxiosRequestConfig | undefined
      )?.skipGlobalErrorMessage;

      if (errorMessage.includes('登录无效')) {
        const userStore = useUserStore();
        userStore.logout();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (!skipGlobalErrorMessage) {
        Message.error({
          content: errorMessage,
          duration: 5 * 1000,
        });
      }
      return Promise.reject(error);
    }
  );
}

attachInterceptors(axios);
attachInterceptors(axiosA);
